import {RefObject, useLayoutEffect} from 'react';

/**
 * Bottom overlay insets (build 128).
 *
 * Docked bars (unsaved-changes bar, mobile bottom navigation) register their
 * height here. Floating layers (upload panel/pill, toasts) add the total to
 * their bottom offset via CSS variables, so they never cover each other:
 *   --overlay-inset-bottom : docked bars (save bar + mobile footer)
 *   --upload-dock-inset    : height of the floating upload pill/panel
 */
type InsetName = 'save-bar' | 'mobile-footer' | 'upload-dock';

const insets = new Map<InsetName, number>();

function apply() {
  if (typeof document === 'undefined') return;
  const root = document.documentElement.style;
  const docked = (insets.get('save-bar') ?? 0) + (insets.get('mobile-footer') ?? 0);
  root.setProperty('--overlay-inset-bottom', `${docked}px`);
  root.setProperty('--upload-dock-inset', `${insets.get('upload-dock') ?? 0}px`);
}

export function setBottomInset(name: InsetName, px: number) {
  if (px > 0) {
    insets.set(name, Math.ceil(px));
  } else {
    insets.delete(name);
  }
  apply();
}

/** Keep the element's height registered while it is mounted and active. */
export function useBottomInset(
  ref: RefObject<HTMLElement | null>,
  name: InsetName,
  active = true,
) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!active || !el) {
      setBottomInset(name, 0);
      return;
    }
    const update = () => setBottomInset(name, el.getBoundingClientRect().height);
    update();
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      setBottomInset(name, 0);
    };
  }, [ref, name, active]);
}
